import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Pencil, Check, X, Upload, ImagePlus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Editable Text Component
export const EditableText = ({ 
  page, 
  section, 
  field, 
  defaultValue = '', 
  className = '',
  as: Component = 'p',
  multiline = false 
}) => {
  const { isAdmin, getAuthHeaders } = useAuth();
  const [value, setValue] = useState(defaultValue);
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(defaultValue);
  const inputRef = useRef(null);

  useEffect(() => {
    loadContent();
  }, [page, section, field]);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${API}/content/${page}/${section}`);
      if (response.data && response.data[field]) {
        setValue(response.data[field]);
        setTempValue(response.data[field]);
      }
    } catch (error) {
      // Use default value
    }
  };

  const saveContent = async () => {
    try {
      // Get current section content first
      let currentContent = {};
      try {
        const response = await axios.get(`${API}/content/${page}/${section}`);
        currentContent = response.data || {};
      } catch (e) {}

      // Update with new field value
      await axios.put(
        `${API}/content/${page}/${section}`,
        { content: { ...currentContent, [field]: tempValue } },
        { headers: getAuthHeaders() }
      );
      setValue(tempValue);
      setIsEditing(false);
      toast.success('Contenu mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const cancelEdit = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  if (!isAdmin) {
    return <Component className={className}>{value}</Component>;
  }

  if (isEditing) {
    return (
      <div className="relative group inline-block w-full">
        {multiline ? (
          <Textarea
            ref={inputRef}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            className={`${className} border-2 border-primary bg-white min-h-[100px]`}
          />
        ) : (
          <Input
            ref={inputRef}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            className={`${className} border-2 border-primary bg-white`}
          />
        )}
        <div className="flex gap-1 mt-2">
          <Button size="sm" onClick={saveContent} className="gap-1">
            <Check className="w-3 h-3" /> Sauver
          </Button>
          <Button size="sm" variant="outline" onClick={cancelEdit} className="gap-1">
            <X className="w-3 h-3" /> Annuler
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group inline-block">
      <Component className={className}>{value}</Component>
      <button
        onClick={() => setIsEditing(true)}
        className="absolute -top-2 -right-2 w-6 h-6 bg-primary text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-lg"
        title="Modifier"
      >
        <Pencil className="w-3 h-3" />
      </button>
    </div>
  );
};

// Editable Image Component
export const EditableImage = ({ 
  page, 
  section, 
  field, 
  defaultSrc = '', 
  alt = '',
  className = '',
  loading = 'lazy',
  fetchPriority,
  width,
  height,
}) => {
  const { isAdmin, getAuthHeaders } = useAuth();
  const [src, setSrc] = useState(defaultSrc);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadContent();
  }, [page, section, field]);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${API}/content/${page}/${section}`);
      if (response.data && response.data[field]) {
        setSrc(response.data[field]);
      }
    } catch (error) {
      // Use default value
    }
  };

  const saveImage = async (imageUrl) => {
    try {
      let currentContent = {};
      try {
        const response = await axios.get(`${API}/content/${page}/${section}`);
        currentContent = response.data || {};
      } catch (e) {}

      await axios.put(
        `${API}/content/${page}/${section}`,
        { content: { ...currentContent, [field]: imageUrl } },
        { headers: getAuthHeaders() }
      );
      setSrc(imageUrl);
      setIsDialogOpen(false);
      setNewUrl('');
      toast.success('Image mise à jour !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders()
        },
      });
      
      await saveImage(response.data.image_url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isAdmin) {
    return <img src={src} alt={alt} className={className} loading={loading} decoding="async" fetchPriority={fetchPriority} width={width} height={height} />;
  }

  return (
    <>
      <div className="relative group">
        <img src={src} alt={alt} className={className} loading={loading} decoding="async" fetchPriority={fetchPriority} width={width} height={height} />
        <button
          onClick={() => setIsDialogOpen(true)}
          className="absolute top-2 right-2 w-10 h-10 bg-primary text-white rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-lg"
          title="Changer l'image"
        >
          <ImagePlus className="w-5 h-5" />
        </button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer l'image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <p className="text-sm font-medium mb-2">Télécharger une image</p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                className="hidden"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full gap-2"
                disabled={isUploading}
              >
                <Upload className="w-4 h-4" />
                {isUploading ? 'Téléchargement...' : 'Choisir un fichier'}
              </Button>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">URL de l'image</p>
              <Input
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://exemple.com/image.jpg"
              />
            </div>

            {(newUrl || src) && (
              <div className="border rounded-lg p-2">
                <p className="text-xs text-muted-foreground mb-2">Aperçu</p>
                <img 
                  src={newUrl || src} 
                  alt="Aperçu" 
                  className="w-full h-32 object-cover rounded"
                />
              </div>
            )}

            <Button 
              onClick={() => newUrl && saveImage(newUrl)}
              className="w-full"
              disabled={!newUrl}
            >
              Sauvegarder l'URL
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Editable List Component (for arrays of items)
export const EditableList = ({
  page,
  section,
  field,
  defaultItems = [],
  renderItem,
  renderEditForm,
  itemSchema = {}
}) => {
  const { isAdmin, getAuthHeaders } = useAuth();
  const [items, setItems] = useState(defaultItems);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newItem, setNewItem] = useState(itemSchema);

  useEffect(() => {
    loadContent();
  }, [page, section, field]);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${API}/content/${page}/${section}`);
      if (response.data && response.data[field]) {
        setItems(response.data[field]);
      }
    } catch (error) {
      // Use default value
    }
  };

  const saveItems = async (newItems) => {
    try {
      let currentContent = {};
      try {
        const response = await axios.get(`${API}/content/${page}/${section}`);
        currentContent = response.data || {};
      } catch (e) {}

      await axios.put(
        `${API}/content/${page}/${section}`,
        { content: { ...currentContent, [field]: newItems } },
        { headers: getAuthHeaders() }
      );
      setItems(newItems);
      toast.success('Liste mise à jour !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleAddItem = async () => {
    const newItems = [...items, { ...newItem, id: Date.now() }];
    await saveItems(newItems);
    setNewItem(itemSchema);
    setIsAddingNew(false);
  };

  const handleUpdateItem = async (index, updatedItem) => {
    const newItems = [...items];
    newItems[index] = updatedItem;
    await saveItems(newItems);
    setEditingIndex(null);
  };

  const handleDeleteItem = async (index) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    const newItems = items.filter((_, i) => i !== index);
    await saveItems(newItems);
  };

  return {
    items,
    isAdmin,
    editingIndex,
    setEditingIndex,
    isAddingNew,
    setIsAddingNew,
    newItem,
    setNewItem,
    handleAddItem,
    handleUpdateItem,
    handleDeleteItem,
  };
};
